import { AitHeaderCellData, AitHeaderGroupData, AitLocationHeaderCell } from "./interface";
import { trGetCellFromLocation } from "./trGetCellFromLocation";

export interface trCanRemoveColSpanReturn<T> {
  canRemove: true;
  reasonNot: undefined;
  targetCell: AitHeaderCellData<T>;
  showCell: AitHeaderCellData<T>;
}

export interface trCanNotRemoveColSpanReturn<T> {
  canRemove: false;
  reasonNot: string;
  targetCell: AitHeaderCellData<T> | undefined;
  showCell: AitHeaderCellData<T> | undefined;
}

/**
 * Determine whether a column span can be reduced (removed) at the specified header cell.
 *
 * @template T - The header cell payload type
 * @param headerData - Header group data containing header rows and cells
 * @param location - Location describing the target header cell
 * @param newColSpan - Desired new colSpan value (must be >= 1)
 * @returns Either a success object (`canRemove: true`) containing the `targetCell` to update
 *          and the `showCell` that will be revealed, or a failure object (`canRemove: false`)
 *          containing `reasonNot` with an explanation why the operation is not permitted.
 */
export const trCanRemoveColSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newColSpan: number,
): trCanRemoveColSpanReturn<T> | trCanNotRemoveColSpanReturn<T> => {
  let reasonNot = "";
  let showCell: AitHeaderCellData<T> | undefined;

  // Check target cell exists
  const targetCell = trGetCellFromLocation(
    {
      headerData: headerData,
      bodyData: [],
      columnInformation: [],
      coreTrafficLight: "BlankNotEvaluated",
    },
    location,
    true,
  ) as AitHeaderCellData<T> | undefined;
  if (targetCell === undefined) {
    reasonNot = "RemoveColSpan: target cell not found";
  } else if (targetCell.colSpan === newColSpan) {
    reasonNot = "";
  } else if (location.isRepeat) {
    reasonNot = "RemoveColSpan: this is a repeat cell";
  } else {
    if (newColSpan < 1) {
      reasonNot = "RemoveColSpan: col span cannot be reduced";
    } else {
      const oldColSpan = targetCell.colSpan;
      // Get the cell to show
      showCell = headerData.rows[location.row]?.cells[location.originalColumn + oldColSpan - 1];
      if (showCell === undefined) {
        reasonNot = "RemoveColSpan: show cell does not exist";
      } else if (showCell.colSpan !== 0) {
        reasonNot = "RemoveColSpan: show cell is not hidden";
      }
    }
  }

  return reasonNot !== ""
    ? { canRemove: false, reasonNot, targetCell, showCell: showCell }
    : {
        canRemove: true,
        reasonNot: undefined,
        targetCell: targetCell!,
        showCell: showCell!,
      };
};

/**
 * Apply a column span removal to header data.
 *
 * This function will set the `colSpan` on the `targetCell` to `newSpan` and
 * set the previously hidden `showCell` to `colSpan: 1`. Use `trCanRemoveColSpan`
 * to validate before calling if you want to avoid exceptions.
 *
 * @template T - The header cell payload type
 * @param headerData - Header group data to modify
 * @param location - Location describing the header cell to change
 * @param newSpan - The new colSpan value to apply
 * @returns Updated `AitHeaderGroupData<T>` with the column span change applied
 * @throws Error when the span cannot be removed (the reason is provided by `trCanRemoveColSpan`)
 */
export const trRemoveColSpan = <T>(
  headerData: AitHeaderGroupData<T>,
  location: AitLocationHeaderCell,
  newSpan: number,
) => {
  // Check if we can remove
  const { canRemove, reasonNot, targetCell, showCell } = trCanRemoveColSpan(
    headerData,
    location,
    newSpan,
  );

  // Already done
  if (targetCell?.colSpan === newSpan) return;

  // Cannot remove
  if (!canRemove) throw new Error(reasonNot);

  // Create return object
  const newHeaderData = { ...headerData };
  newHeaderData.rows = newHeaderData.rows.map((row) => {
    if (row.aitid !== location.rowId) return row;
    const newCells = row.cells.map((cell) => {
      if (cell.aitid === targetCell.aitid)
        return {
          ...cell,
          colSpan: newSpan,
        };
      if (cell.aitid === showCell.aitid) {
        return {
          ...cell,
          colSpan: 1,
        };
      }
      return cell;
    });
    return { ...row, cells: newCells };
  });
  return newHeaderData;
};
